'use client';

import {PepIcon} from '#/features/components/PepIcon';
import {SmallText} from '#/features/components/Text';
import {type Event} from '#/cms/schemas/event.schema';
import dayjs from 'dayjs';
import {getEventAddressString} from '#/utils/get-event-address-string';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {useEffect, useState} from 'react';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export function TimeAndLocation({event}: {event: Event}) {
  const {startDateTime, endDateTime} = event;
  const [clientTimeZone, setClientTimeZone] = useState<string>('Europe/London');

  useEffect(() => {
    // When rendered in client, set the client timezone
    setClientTimeZone(dayjs.tz.guess());
  }, []);

  if (!startDateTime) {
    return null;
  }

  const address = getEventAddressString(event);
  const start = dayjs(startDateTime).tz(clientTimeZone);
  const end = endDateTime ? dayjs(endDateTime).tz(clientTimeZone) : null;
  const startDateTitle = start.format('dddd, D MMMM YYYY');
  const endDateTitle = endDateTime ? dayjs(endDateTime).format('dddd, D MMMM YYYY') : undefined;
  const multiDay = !!end && !start.startOf('day').isSame(end.startOf('day'));
  const startTime = start?.format('HH:mm');
  const endTime = end?.format('HH:mm');
  const tz = start?.format('z');
  const endTimeIsDifferentToStartTime = endTime !== startTime;

  const dates = (
    <SmallText>
      {/* show start date */}
      {startDateTitle}
      {/* if event recurs, show last recurrence date, else show END date only if different from start*/}
      {multiDay ? ` – ${endDateTitle}` : ''}
    </SmallText>
  );

  const times = (
    <SmallText>
      {startTime}
      {end && endTimeIsDifferentToStartTime ? ` – ${endTime}` : ''}
      {!!tz && <span className={'ml-1 text-foreground/75'}>{`(${tz})`}</span>}
    </SmallText>
  );

  return (
    <div className="flex flex-col gap-2">
      {/* time */}
      {start && (
        <div className="flex items-center gap-4">
          <div className="flex h-min w-min items-center justify-center rounded-[0.875rem] bg-primary p-2">
            <PepIcon iconName={'Clock'} strokeColor="background" size={'w-5'} />
          </div>
          <div>
            {dates}
            {times}
          </div>
        </div>
      )}
      <Location address={address} />
    </div>
  );
}

export function Location({address}: {address?: string | null}) {
  if (address) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex h-min w-min items-center justify-center rounded-[0.875rem] bg-primary p-2">
          <PepIcon iconName={'Pin'} strokeColor="background" size={'w-5'} />
        </div>
        <div>
          <SmallText>{address}</SmallText>
        </div>
      </div>
    );
  } else {
    return null;
  }
}
